import { useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trophy } from "lucide-react";
import {
  TOTAL_DAYS,
  isoDate,
  parseISO,
  entryCompletion,
  BADGES,
  computeBadgeStatus,
} from "@/lib/skincare";
import { api } from "@/lib/api";
import PastDaySheet from "@/components/PastDaySheet";

export default function JourneyView({ entries, streak, startDate, dayNum, morningItems, nightItems, photos, waterGoal, onEntryUpdate }) {
  const [sheet, setSheet] = useState(null); // { date, dayNum, entry }
  const [loading, setLoading] = useState(false);

  const grid = useMemo(() => {
    const byDate = {};
    entries.forEach((e) => (byDate[e.date] = e));
    const out = [];
    const start = parseISO(startDate);
    for (let i = 0; i < TOTAL_DAYS; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = isoDate(d);
      const c = entryCompletion(byDate[key], morningItems, nightItems);
      out.push({
        n: i + 1,
        date: key,
        completion: c,
        isToday: i + 1 === dayNum,
        isPast: i + 1 < dayNum,
        entry: byDate[key] || null,
      });
    }
    return out;
  }, [entries, startDate, dayNum, morningItems, nightItems]);

  const openDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const fetched = await api.getEntry(d.date);
      setSheet({ date: d.date, dayNum: d.n, entry: fetched });
    } catch {
      setSheet({ date: d.date, dayNum: d.n, entry: d.entry || null });
    } finally {
      setLoading(false);
    }
  }, []);

  const status = computeBadgeStatus({ entries, photos: photos || [], streak, dayNum, waterGoal, morningItems, nightItems });
  const unlocked = BADGES.map((b) => ({ ...b, on: !!status[b.id] }));
  const unlockedCount = unlocked.filter((b) => b.on).length;

  return (
    <div className="px-6 pt-8 pb-10">
      <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#6B7A70]">
        60-Day Journey
      </div>
      <h2 className="font-heading text-3xl font-semibold text-[#2A332C] tracking-tight">
        Your garden, day by day.
      </h2>

      {/* Grid 60 days */}
      <div className="mt-6 grid grid-cols-7 gap-2" data-testid="journey-grid">
        {grid.map((d) => {
          const color =
            d.completion >= 1
              ? "bg-[#4A6B53] text-white"
              : d.completion >= 0.7
              ? "bg-[#7BA384] text-white"
              : d.completion >= 0.3
              ? "bg-[#CDE0D1] text-[#2A332C]"
              : d.isPast
              ? "bg-[#FADBD8]/50 text-[#CC7A6B]"
              : "bg-white text-[#6B7A70] border border-[#E9EFEA]";
          const isEditable = d.isPast || d.isToday;
          return (
            <motion.button
              key={d.n}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: d.n * 0.01 }}
              whileTap={isEditable ? { scale: 0.92 } : {}}
              onClick={() => isEditable && !loading && openDay(d)}
              className={`aspect-square rounded-xl flex items-center justify-center text-xs font-medium ${color} ${
                d.isToday ? "ring-2 ring-[#2A332C] ring-offset-2 ring-offset-[#F7F5F0]" : ""
              } ${isEditable ? "cursor-pointer" : "cursor-default pointer-events-none"}`}
              title={isEditable ? `Edit day ${d.n} (${d.date})` : d.date}
            >
              {d.n}
            </motion.button>
          );
        })}
      </div>

      {/* Legend + edit hint */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6B7A70]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#4A6B53]"/>Full</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#CDE0D1]"/>Partial</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#FADBD8]/70"/>Missed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-[#E9EFEA] bg-white"/>Upcoming</span>
      </div>
      <p className="mt-2 text-[11px] text-[#6B7A70]/70">
        Tap any past day to edit its routine.
      </p>

      {/* Badges */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#E6B97A]" strokeWidth={1.5} />
            <h3 className="font-heading text-lg font-medium text-[#2A332C]">Milestones</h3>
          </div>
          <span className="text-xs font-semibold text-[#4A6B53]" data-testid="milestone-count">
            {unlockedCount}/{BADGES.length}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="milestones-grid">
          {unlocked.map((b) => (
            <div
              key={b.id}
              data-testid={`milestone-${b.id}`}
              className={`p-4 rounded-2xl border ${
                b.on
                  ? "bg-gradient-to-br from-[#E9F0E8] to-[#FADBD8]/30 border-[#CDE0D1]"
                  : "bg-white/60 border-[#E9EFEA] opacity-60"
              }`}
            >
              <div className="text-2xl mb-1">{b.on ? b.emoji : "🔒"}</div>
              <div className={`font-heading font-medium text-sm ${b.on ? "text-[#2A332C]" : "text-[#6B7A70]"}`}>
                {b.label}
              </div>
              <div className="text-[11px] text-[#6B7A70] mt-0.5 leading-snug">{b.desc}</div>
            </div>
          ))}
        </div>
      </div>
      {sheet && (
        <PastDaySheet
          open={!!sheet}
          onClose={() => setSheet(null)}
          date={sheet.date}
          dayNum={sheet.dayNum}
          initialEntry={sheet.entry}
          morningItems={morningItems}
          nightItems={nightItems}
          onSaved={(date, updatedEntry) => {
            onEntryUpdate?.(date, updatedEntry);
            setSheet((prev) => prev ? { ...prev, entry: updatedEntry } : prev);
          }}
        />
      )}
    </div>
  );
}
